import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { SessionStorageService } from './session-storage.service';
import { PataviumRole } from '../models/request-and-responses/rest-api/enums/role.enum.model';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';


type TIntEvCategory = 'notifications' | 'updated_data';
type TIntEvTopic = 'contract';
type TIntEvType = 'read' | 'done' | 'update_requested';

export interface IInternalEvent {
    category: TIntEvCategory;
    topic: TIntEvTopic;
    type: TIntEvType;
    observable: Observable<IInternalEvent>;
    subject: Subject<IInternalEvent>;
    description: string;
    emit(): void;
}

class InternalEvent implements IInternalEvent {
    // category
    private readonly _category: TIntEvCategory;
    get category(): TIntEvCategory {
        return this._category;
    }

    // topic
    private readonly _topic: TIntEvTopic;
    get topic(): TIntEvTopic {
        return this._topic;
    }

    // type
    private readonly _type: TIntEvType;
    get type(): TIntEvType {
        return this._type;
    }

    // subject
    private readonly _subject: Subject<InternalEvent> = new Subject<InternalEvent>();
    get subject(): Subject<InternalEvent> {
        return this._subject;
    }

    public emit(): void {
        this._subject.next(this);
    }

    get observable(): Observable<InternalEvent> {
        return this._subject.asObservable();
    }

    get description(): string {
        return `${this.category}.${this.topic}.${this.type}`;
    }


    constructor(category: TIntEvCategory, topic: TIntEvTopic, type: TIntEvType) {
        this._category = category;
        this._topic = topic;
        this._type = type;
    }
}


@Injectable()
export class InternalEventsService {

    // events       --> category                --> topic               --> type
    //                  NOTIFICATIONS               CONTRACTS               READ
    //                  UPDATE_DATA                 CONTRACTS               DONE
    //                  UPDATE_DATA                 CONTRACTS               UPDATE_REQUESTED
    EVENTS = {
        NOTIFICATIONS: {
            CONTRACTS: {
                READ: new InternalEvent('notifications', 'contract', 'read')
            }
        },
        UPDATE_DATA: {
            CONTRACTS: {
                DONE: new InternalEvent('updated_data', 'contract', 'done'),
                UPDATE_REQUESTED: new InternalEvent('updated_data', 'contract', 'update_requested')
            }
        }
    };

    constructor(private router: Router) {
        console.log(`Subscribing to ${this.EVENTS.NOTIFICATIONS.CONTRACTS.READ.description}`);
        this.EVENTS.NOTIFICATIONS.CONTRACTS.READ
            .observable
            .pipe(tap(async ev => await this.notificationsContractsReadEventHandler(ev)))
            .subscribe();
    }


    private async notificationsContractsReadEventHandler(event: InternalEvent): Promise<void> {
        console.log(`Handling event ${event.description}`);
        const role = SessionStorageService.sessionInfo.role;
        console.debug(`Handling event ${event.description}: PataviumRole=${PataviumRole[role]}`);
        let route: string = null;
        const topic = event.topic;
        switch (role) {
            case PataviumRole.BackOfficeOperator:
                switch (topic) {
                    case 'contract':
                        route = '/admin/contracts/contracts';
                        break;
                }
                break;
            case PataviumRole.Agency:
                switch (topic) {
                    case 'contract':
                        route = '/agency/contracts/contracts';
                        break;
                }
                break;
            case PataviumRole.Agent:
                switch (topic) {
                    case 'contract':
                        route = '/agent/contracts/contracts';
                        break;
                }
                break;
            case PataviumRole.AgencyOperator:
                switch (topic) {
                    case 'contract':
                        route = '/agency-operator/contracts/contracts';
                        break;
                }
                break;
            default:
                console.error(`Handling event ${event.description}: cannot redirect because role '${role}' is not handled`);
                return;
        }
        if (route == null) {
            console.error(`Handling event ${event.description}: cannot redirect because route is null`);
            return;
        }
        if (this.router.url === route) {
            const update_data_event: InternalEvent = this.EVENTS.UPDATE_DATA.CONTRACTS.UPDATE_REQUESTED;
            console.debug(`Handling event ${event.description}: ${update_data_event.description} event will be emitted`);
            update_data_event.emit();
            return;
        }

        console.debug(`Handling event ${event.description}: redirecting on read message to ${route} from ${this.router.url}`);
        await this.router.navigate([route]);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/internal-events.service.ts